import { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

import {
collection,
addDoc,
updateDoc,
deleteDoc,
doc,
onSnapshot,
serverTimestamp
} from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

import {
Layers,
Trash2,
Edit,
Loader2,
CalendarDays,
LayoutGrid,
List,
Search,
History,
Users,
TrendingUp,
CheckCircle
} from "lucide-react";

import {
Editor,
EditorProvider,
Toolbar,
BtnBold,
BtnItalic,
BtnUnderline,
BtnBulletList,
BtnNumberedList
} from "react-simple-wysiwyg";


export default function AssignProject(){

const navigate = useNavigate();
const { orgId, orgSlug } = useAuth();

const [clients,setClients] = useState([]);
const [employees,setEmployees] = useState([]);
const [projects,setProjects] = useState([]);

const [selectedClient,setSelectedClient] = useState("");
const [serviceNeed,setServiceNeed] = useState("");
const [description,setDescription] = useState("");
const [priority,setPriority] = useState("Medium");
const [dueDate,setDueDate] = useState("");

const [selectedEmployees,setSelectedEmployees] = useState([]);

const [editingId,setEditingId] = useState(null);
const [loading,setLoading] = useState(true);
const [isSubmitting,setIsSubmitting] = useState(false);

const [search,setSearch] = useState("");
const [view,setView] = useState("grid");

const [expanded,setExpanded] = useState({});

const projectRef = collection(db,"organizations",orgId,"projects");
const [showEmployees, setShowEmployees] = useState(null);


/* ---------------- EMPLOYEE OPTIONS ---------------- */

const employeeOptions = useMemo(()=>{

return employees.map(emp=>({
value:emp.id,
label:emp.personal?.fullName || emp.email
}));

},[employees]);


/* ---------------- EMPLOYEE MAP ---------------- */

const employeeMap = useMemo(()=>{

const map = {};

employees.forEach(emp=>{
map[emp.id] = emp.personal?.fullName || emp.email;
});

return map;

},[employees]);


/* ---------------- FIRESTORE LISTENERS ---------------- */

useEffect(()=>{

if(!orgId) return;

const unsubClients = onSnapshot(
collection(db,"organizations",orgId,"clients"),
snap=>{
setClients(snap.docs.map(d=>({id:d.id,...d.data()})));
}
);

const unsubUsers = onSnapshot(
collection(db,"organizations",orgId,"users"),
snap=>{

const emps = snap.docs
.map(d=>({id:d.id,...d.data()}))
.filter(u=>u.role==="employee");

setEmployees(emps);

}
);

const unsubProjects = onSnapshot(projectRef,snap=>{

setProjects(
snap.docs.map(d=>({
id:d.id,
...d.data()
}))
);

setLoading(false);

});

return ()=>{
unsubClients();
unsubUsers();
unsubProjects();
};

},[orgId]);


/* ---------------- CREATE PROJECT ---------------- */

const handleSubmit = async(e)=>{

e.preventDefault();

if(!selectedClient){
alert("Select client");
return;
}

setIsSubmitting(true);

const client = clients.find(c=>c.id===selectedClient);

try{

const data = {

clientId:selectedClient,

clientName:
  client?.name ||
  client?.clientName ||
  "Client",

clientCompany:
  client?.company || "",

clientStatus:
  client?.status || "lead",

clientIndustry:
  client?.industry || "",

serviceNeed,
description,
priority,
dueDate,

assignedEmployees:selectedEmployees,

acceptedBy:[],
progress:0,

createdAt:serverTimestamp()

};

if(editingId){

await updateDoc(
doc(db,"organizations",orgId,"projects",editingId),
data
);

}else{

await addDoc(projectRef,{
...data,
status:"assigned"
});

}

resetForm();

}catch(err){

console.error(err);
alert("Project submission failed");

}finally{

setIsSubmitting(false);

}

};


/* ---------------- RESET FORM ---------------- */

const resetForm=()=>{

setSelectedClient("");
setServiceNeed("");
setDescription("");
setPriority("Medium");
setDueDate("");
setSelectedEmployees([]);
setEditingId(null);

};


/* ---------------- EDIT PROJECT ---------------- */

const editProject=(p)=>{

setEditingId(p.id);
setSelectedClient(p.clientId);
setServiceNeed(p.serviceNeed || "");
setDescription(p.description);
setPriority(p.priority);
setDueDate(p.dueDate);
setSelectedEmployees(p.assignedEmployees || []);

window.scrollTo({top:0,behavior:"smooth"});

};


/* ---------------- DELETE PROJECT ---------------- */

const deleteProject=async(id)=>{

if(window.confirm("Delete this project?")){

await deleteDoc(
doc(db,"organizations",orgId,"projects",id)
);

}

};


/* ---------------- SEARCH FILTER ---------------- */

const filteredProjects = projects.filter(p => {

const term = search.toLowerCase();

return (
p.clientName?.toLowerCase().includes(term) ||
p.serviceNeed?.toLowerCase().includes(term) ||
p.description?.toLowerCase().includes(term) ||
(p.assignedEmployees || []).some(id =>
employeeMap[id]?.toLowerCase().includes(term)
)
);

});


/* ---------------- DESCRIPTION TOGGLE ---------------- */

const toggleReadMore=(id)=>{

setExpanded(prev=>({
...prev,
[id]:!prev[id]
}));

};


/* ---------------- LOADING ---------------- */

if(!orgId || loading){

return(
<div className="flex items-center justify-center h-screen">
<Loader2 className="animate-spin text-indigo-600"/>
</div>
);

}


/* ---------------- UI ---------------- */

return(

<div className="min-h-screen bg-slate-50 pb-20">


{/* HEADER */}

<div className="bg-white border-b shadow-sm">

<div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">

<div className="h-10 w-10 bg-indigo-600 text-white rounded-lg flex items-center justify-center">
<Layers size={20}/>
</div>

<h1 className="text-xl font-semibold">
Project Assignment
</h1>

</div>

</div>


<div className="max-w-7xl mx-auto px-6 mt-8 space-y-10">


{/* CREATE PROJECT */}

<div className="bg-white rounded-xl shadow border p-8">

<h2 className="font-semibold text-lg mb-6">
{editingId ? "Update Project" : "Create Project"}
</h2>

<form onSubmit={handleSubmit} className="space-y-6">

<select
value={selectedClient}
onChange={e=>setSelectedClient(e.target.value)}
className="w-full border p-3 rounded-lg"
required
>

<option value="">Select Client</option>

{clients.map(c=>(
<option key={c.id} value={c.id}>
{c.name || c.clientName}
</option>
))}

</select>


<input
type="text"
placeholder="Service Need (Fabrication / Welding / Installation)"
value={serviceNeed}
onChange={(e)=>setServiceNeed(e.target.value)}
className="w-full border p-3 rounded-lg"
/>


<Select
isMulti
options={employeeOptions}
placeholder="Assign Employees..."
value={employeeOptions.filter(opt =>
selectedEmployees.includes(opt.value)
)}
onChange={sel =>
setSelectedEmployees(
sel ? sel.map(s=>s.value) : []
)
}
/>


<EditorProvider>

<div className="border rounded-lg bg-white">

<Toolbar>
<BtnBold/>
<BtnItalic/>
<BtnUnderline/>
<BtnBulletList/>
<BtnNumberedList/>
</Toolbar>

<Editor
value={description}
onChange={(e)=>setDescription(e.target.value)}
placeholder="Project description..."
className="min-h-[120px] p-3"
/>

</div>

</EditorProvider>


<div className="grid md:grid-cols-3 gap-4">

<select
value={priority}
onChange={e=>setPriority(e.target.value)}
className="border p-3 rounded-lg"
>
<option>Low</option>
<option>Medium</option>
<option>High</option>
</select>

<input
type="date"
value={dueDate}
onChange={e=>setDueDate(e.target.value)}
className="border p-3 rounded-lg"
/>

</div>


<button
disabled={isSubmitting}
className="px-8 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
>

{isSubmitting && <Loader2 className="animate-spin" size={16}/>}

{editingId ? "Update Project" : "Assign Project"}

</button>

</form>

</div>


{/* SEARCH + VIEW */}

<div className="flex justify-between items-center">

<div className="relative w-80">

<Search
size={16}
className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
/>

<input
type="text"
placeholder="Search client, employee, task..."
value={search}
onChange={(e)=>setSearch(e.target.value)}
className="w-full border rounded-xl pl-10 pr-4 py-3 bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none"
/>

</div>


<div className="flex gap-2">

<button
onClick={()=>setView("grid")}
className={`p-2 rounded ${view==="grid"?"bg-indigo-600 text-white":"bg-white border"}`}
>
<LayoutGrid size={18}/>
</button>

<button
onClick={()=>setView("list")}
className={`p-2 rounded ${view==="list"?"bg-indigo-600 text-white":"bg-white border"}`}
>
<List size={18}/>
</button>

</div>

</div>


{/* PROJECT LIST */}

<div className={view==="grid"
? "grid sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
: "space-y-4"}>


{filteredProjects.map(p=>{

const text = p.description || "";
const shortText = text.substring(0,180);

return(

<div
key={p.id}
className="bg-white border rounded-xl p-6 shadow-sm hover:shadow-md transition"
>


<div className="flex justify-between">

<div>

<h3 className="font-semibold text-lg text-indigo-700 flex items-center gap-2">
<Layers size={16}/>
{p.serviceNeed || "Service"}
</h3>

<p className="text-xs text-gray-500">
Client: {p.clientName}
</p>


</div>


<div className="flex gap-3">

<button
onClick={()=>navigate(`/${orgSlug}/project-history/${p.id}`)}
className="text-gray-500 hover:text-indigo-600"
>
<History size={18}/>
</button>

<button
onClick={()=>editProject(p)}
className="text-blue-600"
>
<Edit size={16}/>
</button>

<button
onClick={()=>deleteProject(p.id)}
className="text-red-600"
>
<Trash2 size={16}/>
</button>

</div>

</div>


<div className="prose prose-sm max-w-none text-gray-700 mt-3">

<div
dangerouslySetInnerHTML={{
__html: expanded[p.id] ? text : shortText
}}
/>

{text.length > 180 && (

<button
onClick={()=>toggleReadMore(p.id)}
className="text-indigo-600 text-xs mt-1"
>
{expanded[p.id] ? "Read Less" : "Read More"}
</button>

)}

</div>


<div className="flex items-center gap-2 text-xs text-gray-500 mt-4">
<CalendarDays size={14}/>
{p.dueDate || "No due date"}
</div>


<div className="flex justify-between items-center mt-4 text-xs text-gray-500">
<button
  onClick={() => setShowEmployees(p)}
  className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600"
>
  <Users size={14}/>
  {p.assignedEmployees?.length || 0} Employees
</button>
<div className="flex items-center gap-1">
<TrendingUp size={14}/>
{p.progress || 0}% Progress
</div>
{showEmployees && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">

    <div className="bg-white rounded-xl p-6 w-80 shadow-lg">

      <h2 className="text-lg font-semibold mb-4">
        Assigned Employees
      </h2>

      <div className="space-y-2 max-h-60 overflow-y-auto">

        {showEmployees.assignedEmployees?.length > 0 ? (
          showEmployees.assignedEmployees.map((empId, index) => (
            <div
              key={index}
              className="p-2 border rounded-lg text-sm"
            >
              {employeeMap[empId] || "Unknown"}
            </div>
          ))
        ) : (
          <p className="text-gray-500 text-sm">
            No employees assigned
          </p>
        )}

      </div>

      <button
        onClick={() => setShowEmployees(null)}
        className="mt-4 w-full bg-indigo-600 text-white py-2 rounded-lg"
      >
        Close
      </button>

    </div>

  </div>
)}
</div>


{p.acceptedBy?.length > 0 && (

<div className="mt-4">

<p className="text-xs text-gray-500 mb-1">
Accepted Employees
</p>

<div className="flex flex-wrap gap-2">

{p.acceptedBy.map(emp => (

<span
key={emp.id}
className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full"
>

<CheckCircle size={12}/>
{emp.name}

</span>

))}

</div>

</div>

)}

</div>

);

})}

</div>

</div>

</div>

);

}